import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  INVOICE_STATUSES,
  Invoice,
  InvoiceDetail,
  InvoiceStatus,
  MarginReport,
  OrgBilling,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth";

function money(n: number, dp = 2): string {
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;
}

function gb(n: number): string {
  return `${n.toLocaleString(undefined, {
    maximumFractionDigits: n < 10 ? 1 : 0,
  })} GB`;
}

/** Invoices for periods that closed before storage billing existed carry zeros
 *  in every storage column. That is honest data, but rendering it as
 *  "0 GB of 0 GB" reads like a broken meter rather than "not billed for". */
function hasStorageTerms(inv: Invoice): boolean {
  return (
    inv.included_storage_gb > 0 ||
    inv.storage_overage_usd_per_gb > 0 ||
    inv.peak_storage_gb > 0
  );
}

function fmtDate(d: string | null): string {
  if (!d) return "-";
  return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Which statuses an invoice can legally move to. Draft is the only entry
 *  point; void is terminal and reachable from anything unpaid. */
const NEXT_STATUS: Record<InvoiceStatus, InvoiceStatus[]> = {
  draft: ["approved", "void"],
  approved: ["sent", "draft", "void"],
  sent: ["paid", "approved", "void"],
  paid: ["sent"],
  void: ["draft"],
};

export default function AdminInvoices() {
  const me = useAuth((s) => s.user);
  const isOwner = me?.role === "owner";

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [orgs, setOrgs] = useState<OrgBilling[]>([]);
  const [margin, setMargin] = useState<MarginReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [orgFilter, setOrgFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingDetail, setSavingDetail] = useState(false);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (orgFilter) p.set("organization_id", orgFilter);
    if (statusFilter) p.set("status", statusFilter);
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    return p.toString();
  }, [orgFilter, statusFilter, start, end]);

  useEffect(() => {
    if (!isOwner) return;
    void load();
  }, [isOwner, query]);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      const qs = query ? `?${query}` : "";
      const [inv, marginRep] = await Promise.all([
        api.get<Invoice[]>(`/admin/billing/invoices${qs}`),
        api.get<MarginReport>("/admin/billing/margin"),
      ]);
      setInvoices(inv);
      setMargin(marginRep);
      if (orgs.length === 0) {
        setOrgs(await api.get<OrgBilling[]>("/admin/billing/orgs"));
      }
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load invoices");
    } finally {
      setBusy(false);
    }
  }

  function flashFor(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash((m) => (m === msg ? null : m)), 4000);
  }

  async function openDetail(inv: Invoice) {
    setDetailBusy(true);
    setErr(null);
    try {
      const d = await api.get<InvoiceDetail>(
        `/admin/billing/invoices/${inv.id}`,
      );
      setDetail(d);
      setNoteDraft(d.notes ?? "");
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load invoice");
    } finally {
      setDetailBusy(false);
    }
  }

  async function setStatus(inv: Invoice, next: InvoiceStatus) {
    setErr(null);
    try {
      await api.patch<Invoice>(`/admin/billing/invoices/${inv.id}`, {
        status: next,
      });
      flashFor(`${inv.invoice_number} marked ${next}.`);
      if (detail?.id === inv.id) {
        setDetail({ ...detail, status: next });
      }
      await load();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to update invoice");
    }
  }

  async function saveNotes() {
    if (!detail) return;
    setSavingDetail(true);
    setErr(null);
    try {
      await api.patch<Invoice>(`/admin/billing/invoices/${detail.id}`, {
        notes: noteDraft,
      });
      flashFor(`Saved notes on ${detail.invoice_number}.`);
      setDetail({ ...detail, notes: noteDraft });
      await load();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to save notes");
    } finally {
      setSavingDetail(false);
    }
  }

  /** The export endpoint is authenticated, so pull it through the API client
   *  as a blob - a plain link would arrive without the bearer token. */
  async function exportCsv() {
    setErr(null);
    try {
      const qs = query ? `?${query}` : "";
      const blob = await api.blob(`/admin/billing/invoices.csv${qs}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Export failed");
    }
  }

  const totals = useMemo(() => {
    const billable = invoices.filter((i) => i.status !== "void");
    return {
      count: invoices.length,
      creditsUsed: billable.reduce((s, i) => s + i.credits_used, 0),
      overageCredits: billable.reduce((s, i) => s + i.overage_credits, 0),
      storageOverageGb: billable.reduce((s, i) => s + i.storage_overage_gb, 0),
      peakStorageGb: billable.reduce((s, i) => s + i.peak_storage_gb, 0),
      // Still summed, but only as the denominator-free half of the internal
      // margin ratio below -- it is never rendered as a charge.
      revenue: billable.reduce((s, i) => s + i.total_amount_usd, 0),
      cost: billable.reduce((s, i) => s + (i.upstream_cost_usd ?? 0), 0),
      unpriced: billable.reduce((s, i) => s + i.unpriced_calls, 0),
    };
  }, [invoices]);

  if (!isOwner) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Billing</div>
            <h1>Invoices</h1>
          </div>
        </header>
        <div className="alert alert--error" role="alert">
          Invoices are restricted to owners.
        </div>
      </div>
    );
  }

  const marginMultiple =
    totals.cost > 0 ? totals.revenue / totals.cost : null;

  return (
    <div className="admin admin--page-scroll">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Billing</div>
          <h1>Usage statements</h1>
          <p className="admin__lead">
            One statement per customer per closed billing period, drafted
            automatically by the billing sweeper. Consumption is frozen at
            close, so later tier edits never change what a period recorded.
            Invoicing itself happens in a separate system, so no charges are
            shown here.
          </p>
        </div>
        <div className="admin__head-actions">
          <button type="button" className="btn" onClick={() => void exportCsv()}>
            Export CSV
          </button>
          <button type="button" className="btn" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </header>

      {err && <div className="alert alert--error">{err}</div>}
      {flash && <div className="alert alert--ok">{flash}</div>}

      <div className="billing-stats">
        <div className="billing-stat">
          <span className="billing-stat__label">Statements</span>
          <span className="billing-stat__value">{totals.count}</span>
        </div>
        <div className="billing-stat">
          <span className="billing-stat__label">Credits used</span>
          <span className="billing-stat__value">
            {totals.creditsUsed.toLocaleString()}
          </span>
          <span className="billing-stat__hint">
            {totals.overageCredits > 0
              ? `${totals.overageCredits.toLocaleString()} past allowance`
              : "all within allowance"}
          </span>
        </div>
        <div className="billing-stat">
          <span className="billing-stat__label">Storage over</span>
          <span className="billing-stat__value">
            {gb(totals.storageOverageGb)}
          </span>
          <span className="billing-stat__hint">
            {gb(totals.peakStorageGb)} peak across these statements
          </span>
        </div>
        <div className="billing-stat">
          <span className="billing-stat__label">Our AI cost</span>
          <span className="billing-stat__value">{money(totals.cost)}</span>
          <span className="billing-stat__hint">
            {totals.unpriced > 0
              ? `${totals.unpriced.toLocaleString()} call(s) had no rate-card price, so this is understated`
              : "every call was priced"}
          </span>
        </div>
        <div className="billing-stat">
          <span className="billing-stat__label">Margin</span>
          <span className="billing-stat__value">
            {marginMultiple == null ? "-" : `${marginMultiple.toFixed(2)}x`}
          </span>
          <span className="billing-stat__hint">billed divided by cost</span>
        </div>
      </div>

      <div className="admin-card">
        <div className="billing-filters">
          <div className="admin-card__field">
            <label htmlFor="if-org">Customer</label>
            <select
              id="if-org"
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
            >
              <option value="">All customers</option>
              {orgs.map((o) => (
                <option key={o.organization_id} value={o.organization_id}>
                  {o.organization_name}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-card__field">
            <label htmlFor="if-status">Status</label>
            <select
              id="if-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Any status</option>
              {INVOICE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-card__field">
            <label htmlFor="if-start">Period from</label>
            <input
              id="if-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="admin-card__field">
            <label htmlFor="if-end">Period to</label>
            <input
              id="if-end"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setOrgFilter("");
              setStatusFilter("");
              setStart("");
              setEnd("");
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="admin__scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Statement</th>
              <th>Customer</th>
              <th>Period</th>
              <th>Tier</th>
              <th>Credits</th>
              <th>Overage</th>
              <th>Storage</th>
              <th>Cost</th>
              <th>Margin</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr>
                <td colSpan={11} className="muted">
                  Loading...
                </td>
              </tr>
            )}
            {!busy && invoices.length === 0 && (
              <tr>
                <td colSpan={11} className="muted">
                  No statements match these filters. Statements appear once a
                  billing period closes.
                </td>
              </tr>
            )}
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td>
                  <button
                    type="button"
                    className="btn btn--link admin-table__name"
                    onClick={() => void openDetail(inv)}
                  >
                    {inv.invoice_number}
                  </button>
                  <div className="admin-table__mono muted">
                    {fmtDate(
                      inv.generated_at ? inv.generated_at.slice(0, 10) : null,
                    )}
                  </div>
                </td>
                <td>{inv.organization_name ?? <span className="muted">(deleted)</span>}</td>
                <td className="billing-num">
                  {fmtDate(inv.period_start)}
                  <div className="muted admin-table__mono">
                    to {fmtDate(inv.period_end)}
                  </div>
                </td>
                <td>{inv.tier_name ?? <span className="muted">-</span>}</td>
                <td className="billing-num">
                  {inv.credits_used.toLocaleString()}
                  <div className="muted admin-table__mono">
                    of {inv.included_credits.toLocaleString()}
                  </div>
                </td>
                <td className="billing-num">
                  {inv.overage_credits > 0 ? (
                    <span className="billing-num--over">
                      {inv.overage_credits.toLocaleString()}
                    </span>
                  ) : (
                    <span className="muted">-</span>
                  )}
                </td>
                <td className="billing-num">
                  {hasStorageTerms(inv) ? (
                    <>
                      {gb(inv.peak_storage_gb)}
                      <div className="muted admin-table__mono">
                        {inv.storage_overage_gb > 0
                          ? `${gb(inv.storage_overage_gb)} over ${gb(
                              inv.included_storage_gb,
                            )}`
                          : `of ${gb(inv.included_storage_gb)}`}
                      </div>
                    </>
                  ) : (
                    <span className="muted" title="This period closed before storage was metered.">
                      not metered
                    </span>
                  )}
                </td>
                <td className="billing-num">
                  {inv.upstream_cost_usd == null ? (
                    <span className="muted">-</span>
                  ) : (
                    money(inv.upstream_cost_usd, 2)
                  )}
                </td>
                <td className="billing-num">
                  {inv.margin_multiple == null ? (
                    <span className="muted">-</span>
                  ) : (
                    `${inv.margin_multiple.toFixed(2)}x`
                  )}
                </td>
                <td>
                  <span className={`invoice-status invoice-status--${inv.status}`}>
                    {inv.status}
                  </span>
                </td>
                <td className="admin-table__actions">
                  {NEXT_STATUS[inv.status].slice(0, 1).map((next) => (
                    <button
                      key={next}
                      type="button"
                      className="btn"
                      onClick={() => void setStatus(inv, next)}
                    >
                      Mark {next}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="btn"
                    onClick={() => void openDetail(inv)}
                    disabled={detailBusy}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {margin && margin.by_tier.length > 0 && (
        <div className="admin-card">
          <div className="admin-card__head">
            <div>
              <h2 className="admin-card__title">Cost by tier</h2>
              <p className="admin-card__blurb">
                Our own AI spend on closed periods - what the work cost us, not
                what anyone is charged. Calls whose model is missing from the
                rate card are excluded and counted separately, so an
                understated cost is visible rather than silently flattering the
                margin.
              </p>
            </div>
          </div>
          <div className="admin__scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Statements</th>
                  <th>Credits</th>
                  <th>Cost</th>
                  <th>Cost / credit</th>
                  <th>Margin</th>
                  <th>Unpriced calls</th>
                </tr>
              </thead>
              <tbody>
                {margin.by_tier.map((r) => (
                  <tr key={r.tier_name ?? "none"}>
                    <td>
                      <div className="admin-table__name">
                        {r.tier_name ?? "(no tier)"}
                      </div>
                    </td>
                    <td className="billing-num">{r.invoices}</td>
                    <td className="billing-num">
                      {r.credits_used.toLocaleString()}
                    </td>
                    <td className="billing-num">
                      {r.cost_usd == null ? "-" : money(r.cost_usd)}
                    </td>
                    <td className="billing-num">
                      {r.cost_per_credit == null
                        ? "-"
                        : money(r.cost_per_credit, 4)}
                    </td>
                    <td className="billing-num">
                      {r.margin_multiple == null
                        ? "-"
                        : `${r.margin_multiple.toFixed(2)}x`}
                    </td>
                    <td className="billing-num">
                      {r.unpriced_calls > 0 ? (
                        r.unpriced_calls.toLocaleString()
                      ) : (
                        <span className="muted">none</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------- invoice detail ---------- */}
      {detail && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDetail(null);
          }}
        >
          <div
            className="modal__card modal__card--lg modal__card--split"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal__header">
              <div className="modal__header-main">
                <h2 className="modal__header-title">
                  {detail.invoice_number}
                </h2>
                <div className="muted">
                  {detail.organization_name ?? "(deleted organization)"} &middot;{" "}
                  {fmtDate(detail.period_start)} to {fmtDate(detail.period_end)}{" "}
                  &middot;{" "}
                  <span
                    className={`invoice-status invoice-status--${detail.status}`}
                  >
                    {detail.status}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="modal__close"
                onClick={() => setDetail(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="modal__body">
              <div className="billing-stats">
                <div className="billing-stat">
                  <span className="billing-stat__label">
                    {detail.tier_name ?? "Plan"}
                  </span>
                  <span className="billing-stat__value">
                    {detail.credits_used.toLocaleString()}
                  </span>
                  <span className="billing-stat__hint">
                    credits used of {detail.included_credits.toLocaleString()}{" "}
                    included
                  </span>
                </div>
                <div className="billing-stat">
                  <span className="billing-stat__label">Overage</span>
                  <span className="billing-stat__value">
                    {detail.overage_credits.toLocaleString()}
                  </span>
                  <span className="billing-stat__hint">
                    credits past the allowance
                  </span>
                </div>
                {hasStorageTerms(detail) && (
                  <div className="billing-stat">
                    <span className="billing-stat__label">Storage</span>
                    <span className="billing-stat__value">
                      {gb(detail.peak_storage_gb)}
                    </span>
                    <span className="billing-stat__hint">
                      peak of {gb(detail.included_storage_gb)} included
                      {detail.storage_overage_gb > 0
                        ? `, ${gb(detail.storage_overage_gb)} over`
                        : ""}
                      {detail.peak_storage_on
                        ? ` (${fmtDate(detail.peak_storage_on)})`
                        : ""}
                    </span>
                  </div>
                )}
                <div className="billing-stat">
                  <span className="billing-stat__label">Our cost</span>
                  <span className="billing-stat__value">
                    {detail.upstream_cost_usd == null
                      ? "-"
                      : money(detail.upstream_cost_usd)}
                  </span>
                  <span className="billing-stat__hint">
                    {detail.cost_per_credit == null
                      ? "no measured cost for this period"
                      : `${money(detail.cost_per_credit, 4)} per credit`}
                  </span>
                </div>
                <div className="billing-stat">
                  <span className="billing-stat__label">Margin</span>
                  <span className="billing-stat__value">
                    {detail.margin_multiple == null
                      ? "-"
                      : `${detail.margin_multiple.toFixed(2)}x`}
                  </span>
                  <span className="billing-stat__hint">
                    modelled plan value over our cost
                  </span>
                </div>
              </div>

              {detail.unpriced_calls > 0 && (
                <div className="alert alert--warning">
                  {detail.unpriced_calls.toLocaleString()} AI call
                  {detail.unpriced_calls === 1 ? "" : "s"} in this period ran on
                  a model that is not in the rate card, so the cost and margin
                  above are understated.
                </div>
              )}

              <h3 className="modal__section-title">Credit breakdown</h3>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Activity</th>
                    <th>Volume</th>
                    <th>Times</th>
                    <th>Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.length === 0 && (
                    <tr>
                      <td colSpan={4} className="muted">
                        No credit activity recorded for this period.
                      </td>
                    </tr>
                  )}
                  {detail.lines.map((l) => (
                    <tr key={l.action_key}>
                      <td>
                        <div className="admin-table__name">{l.label}</div>
                      </td>
                      <td className="billing-num">
                        {Number.isInteger(l.quantity)
                          ? l.quantity.toLocaleString()
                          : l.quantity.toFixed(1)}{" "}
                        {l.unit}
                        {l.quantity === 1 ? "" : "s"}
                      </td>
                      <td className="billing-num">
                        {l.events.toLocaleString()}
                      </td>
                      <td className="billing-num">
                        {l.credits.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="admin-card__field admin-card__field--wide">
                <label htmlFor="inv-notes">Notes</label>
                <textarea
                  id="inv-notes"
                  rows={3}
                  maxLength={1024}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                />
              </div>
            </div>

            <footer className="modal__footer">
              <span className="muted modal__footer-hint">
                {detail.approved_by_name
                  ? `Approved by ${detail.approved_by_name}`
                  : "Not yet approved"}
              </span>
              <div className="modal__footer-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => void saveNotes()}
                  disabled={savingDetail || noteDraft === (detail.notes ?? "")}
                >
                  {savingDetail ? "Saving..." : "Save notes"}
                </button>
                {NEXT_STATUS[detail.status].map((next) => (
                  <button
                    key={next}
                    type="button"
                    className={
                      next === "void" ? "btn btn--danger" : "btn btn--primary"
                    }
                    onClick={() => void setStatus(detail, next)}
                  >
                    Mark {next}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn"
                  onClick={() => setDetail(null)}
                >
                  Close
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
